'use client';

import { Button } from '@/features/uiKit/Button';
import { Link } from '@/features/uiKit/Link';
import { Stack } from '@/features/uiKit/Stack';
import { Text } from '@/features/uiKit/Text';
import { useLingui } from '@lingui/react';
import { landingContentFrame } from '@/features/Landing/landingSettings';
import { getAppUrlStart, getUrlStart } from '@/features/Lang/getUrlStart';
import {
  fullEnglishLanguageName,
  SupportedLanguage,
  supportedLanguagesToLearn,
} from '@/features/Lang/lang';
import { GeneralFaqBlock } from '@/features/Landing/FAQ/GeneralFaqBlock';
import { CtaBlock } from '@/features/Landing/ctaBlock';
import { FaqItemInfo } from '@/features/Landing/FAQ/FaqItem';
import { Languages, MessageCircle, Mic, Sparkles, Zap } from 'lucide-react';
import { useRef } from 'react';
import { AliasCtaButton } from './AliasCtaButton';
import { AliasHeroDemo } from './AliasHeroDemo';
import { AliasResultPreview } from './AliasResultPreview';
import { AliasStickyCta } from './AliasStickyCta';

const HERO_CTA_ID = 'alias-hero-cta';
const PRACTICE_PATH = 'practice?rolePlayId=alias-game';

interface AliasLandingPageProps {
  lang: SupportedLanguage;
}

const stepIcons = [Languages, Mic, Sparkles] as const;

export const AliasLandingPage = ({ lang }: AliasLandingPageProps) => {
  const { i18n } = useLingui();
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const practiceUrl = getAppUrlStart(lang) + PRACTICE_PATH;
  const privacyUrl = `${getUrlStart(lang)}privacy`;
  const pricingUrl = `${getUrlStart(lang)}pricing`;

  const learnableLanguages = supportedLanguagesToLearn
    .map((code) => fullEnglishLanguageName[code])
    .join(', ');

  const faqItems: FaqItemInfo[] = [
    {
      question: i18n._('Do I need a microphone?'),
      answer: (
        <Text color="secondary">
          {i18n._(
            'Yes. The Alias game uses your microphone so you can describe words aloud. Your browser asks for permission when you start your first round — not when you open this page.',
          )}
        </Text>
      ),
    },
    {
      question: i18n._('Do I need an account?'),
      answer: (
        <Text color="secondary">
          {i18n._(
            'Yes. Sign in to FluencyPal to play the Alias game and save your progress. After you sign in, you return to the same Alias practice session.',
          )}
        </Text>
      ),
    },
    {
      question: i18n._('Is the game free?'),
      answer: (
        <Text color="secondary">
          {i18n._('You can start on FluencyPal’s ')}
          <Link href={pricingUrl} underline="hover" color="inherit">
            {i18n._('free plan')}
          </Link>
          {i18n._(
            ' with limited speaking messages. Unlimited practice and full features require a paid subscription.',
          )}
        </Text>
      ),
    },
    {
      question: i18n._('What happens to my recording?'),
      answer: (
        <Text color="secondary">
          {i18n._(
            'Voice audio is processed to generate transcripts and feedback. FluencyPal does not store your voice recordings. Conversation transcripts may be saved in your account to track progress. See our ',
          )}
          <Link href={privacyUrl} underline="hover" color="inherit" target="_blank">
            {i18n._('Privacy Policy')}
          </Link>
          {i18n._(' for details.')}
        </Text>
      ),
    },
    {
      question: i18n._('Which languages can I practise?'),
      answer: (
        <Text color="secondary">
          {i18n._(
            'You practise speaking in the language you are learning in FluencyPal. Supported learning languages include ',
          )}
          {learnableLanguages}
          {i18n._(
            '. The Alias game adapts word difficulty to the level you choose at the start of each round.',
          )}
        </Text>
      ),
    },
  ];

  const steps = [
    {
      title: i18n._('Choose your level'),
      text: i18n._('Get words appropriate for your current vocabulary.'),
    },
    {
      title: i18n._('Describe the word'),
      text: i18n._('Explain it aloud without using the word itself.'),
    },
    {
      title: i18n._('See the AI\u2019s guess'),
      text: i18n._('Find out whether your explanation worked and review your feedback.'),
    },
  ];

  const benefits = [
    i18n._('Recall vocabulary without translating every sentence.'),
    i18n._('Explain ideas when you do not know the exact word.'),
    i18n._('Speak spontaneously instead of reading prepared answers.'),
    i18n._('Become more comfortable thinking in your target language.'),
  ];

  const primaryCtaLabel = i18n._('Play a Round with AI');
  const stickyCtaLabel = i18n._('Play with AI');

  return (
    <Stack
      component="article"
      sx={{
        width: '100%',
        color: '#222',
        paddingBottom: '88px',
        '@media (min-width: 900px)': { paddingBottom: 0 },
      }}
    >
      {/* 1. Hero */}
      <Stack
        component="section"
        sx={{
          width: '100%',
          alignItems: 'center',
          marginTop: '-28px',
          padding: '52px 0 98px',
          backgroundImage:
            'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(231, 222, 203, 0.3) 100%)',
          '@media (min-width: 900px)': { padding: '68px 0 92px' },
        }}
      >
        <Stack
          sx={{
            ...landingContentFrame,
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '36px',
            alignItems: 'center',
            '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr', gap: '48px' },
          }}
        >
          <Stack
            sx={{
              gap: '20px',
              alignItems: 'center',
              textAlign: 'center',
              '@media (min-width: 900px)': { alignItems: 'flex-start', textAlign: 'left' },
            }}
          >
            <Text variant="label" color="secondary">
              {i18n._('AI speaking game')}
            </Text>

            <Stack sx={{ gap: '8px' }}>
              <Text as="h1" variant="display">
                {i18n._('Can the AI Guess Your Word?')}
              </Text>
              <Text variant="small" color="secondary">
                {i18n._('Alias Word Guessing Game')}
              </Text>
            </Stack>

            <Stack sx={{ maxWidth: '520px' }}>
              <Text variant="lead" color="secondary">
                {i18n._(
                  'Describe a word without saying it. Speak naturally, let your AI partner guess, and improve your vocabulary and fluency.',
                )}
              </Text>
            </Stack>

            <Stack
              ref={heroCtaRef}
              id={HERO_CTA_ID}
              sx={{
                gap: '12px',
                width: '100%',
                alignItems: 'center',
                paddingTop: '8px',
                '@media (min-width: 900px)': { alignItems: 'flex-start' },
              }}
            >
              <Stack
                sx={{
                  flexDirection: 'column',
                  gap: '12px',
                  alignItems: 'center',
                  width: '100%',
                  '@media (min-width: 600px)': { flexDirection: 'row', width: 'auto' },
                }}
              >
                <AliasCtaButton href={practiceUrl}>{primaryCtaLabel}</AliasCtaButton>
                <Stack
                  sx={{
                    width: '100%',
                    maxWidth: '400px',
                    '@media (min-width: 600px)': { width: 'auto', maxWidth: 'none' },
                  }}
                >
                  <Button href="#how-it-works" tone="secondary" fullWidth>
                    {i18n._('See How It Works')}
                  </Button>
                </Stack>
              </Stack>

              <Text variant="small" color="secondary">
                {i18n._('Beginner to fluent · Uses your microphone · Works in your browser')}
              </Text>
            </Stack>
          </Stack>

          <Stack sx={{ alignItems: 'center', justifyContent: 'center' }}>
            <AliasHeroDemo />
          </Stack>
        </Stack>
      </Stack>

      {/* 2. Three-step explanation */}
      <Stack
        id="how-it-works"
        component="section"
        sx={{
          width: '100%',
          alignItems: 'center',
          padding: '86px 0',
          backgroundColor: '#fff',
          '@media (min-width: 900px)': { padding: '122px 0' },
        }}
      >
        <Stack sx={{ ...landingContentFrame, gap: '40px', alignItems: 'center' }}>
          <Text as="h2" variant="title" align="center">
            {i18n._('One round. Three simple steps.')}
          </Text>

          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '24px',
              width: '100%',
              '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr 1fr' },
            }}
          >
            {steps.map((step, index) => {
              const Icon = stepIcons[index] ?? MessageCircle;
              return (
                <Stack
                  key={step.title}
                  sx={{
                    gap: '12px',
                    padding: '24px',
                    borderRadius: '16px',
                    backgroundColor: 'rgb(255, 253, 249)',
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Stack
                    sx={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(43, 35, 88, 0.08)',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgb(43 35 88)',
                    }}
                  >
                    <Icon size={20} aria-hidden="true" />
                  </Stack>
                  <Stack>
                    <Text as="h3">
                      {index + 1}. {step.title}
                    </Text>
                    <Text color="secondary">{step.text}</Text>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>

          <AliasCtaButton href={practiceUrl}>{i18n._('Start My First Round')}</AliasCtaButton>
        </Stack>
      </Stack>

      {/* 3. Result preview */}
      <Stack
        component="section"
        sx={{
          width: '100%',
          alignItems: 'center',
          padding: '56px 0',
          backgroundColor: 'rgba(231, 222, 203, 0.3)',
          '@media (min-width: 900px)': { padding: '92px 0' },
        }}
      >
        <Stack
          sx={{
            ...landingContentFrame,
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '40px',
            alignItems: 'center',
            '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
          }}
        >
          <Stack sx={{ gap: '16px' }}>
            <Text as="h2" variant="title">
              {i18n._('See the AI\u2019s Guess')}
            </Text>
            <Stack sx={{ maxWidth: '480px' }}>
              <Text variant="lead" color="secondary">
                {i18n._(
                  'Every round ends with a clear result: the word the AI guessed, whether you were right, and short feedback on how you explained it.',
                )}
              </Text>
            </Stack>
          </Stack>
          <Stack sx={{ alignItems: 'center' }}>
            <AliasResultPreview />
          </Stack>
        </Stack>
      </Stack>

      {/* 4. Learning benefits */}
      <Stack
        component="section"
        sx={{
          width: '100%',
          alignItems: 'center',
          padding: '98px 0',
          backgroundColor: '#fff',
          '@media (min-width: 900px)': { padding: '104px 0' },
        }}
      >
        <Stack sx={{ ...landingContentFrame, gap: '20px' }}>
          <Text as="h2" variant="title">
            {i18n._('Practice the skills real conversations require')}
          </Text>

          <Stack
            component="ul"
            sx={{
              gap: '12px',
              margin: 0,
              padding: 0,
              listStyle: 'none',
            }}
          >
            {benefits.map((benefit) => (
              <Stack
                component="li"
                key={benefit}
                sx={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <Stack sx={{ paddingTop: '4px', color: 'rgb(43 35 88)', flexShrink: 0 }}>
                  <Zap size={18} aria-hidden="true" />
                </Stack>
                <Text>{benefit}</Text>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>

      <GeneralFaqBlock
        id="alias-faq"
        title={i18n._('Common questions')}
        subTitle={i18n._('Your questions answered')}
        padding="80px 0 72px"
        items={faqItems}
      />

      <CtaBlock
        title={i18n._('Ready to see whether the AI can guess your word?')}
        actionButtonTitle={primaryCtaLabel}
        actionButtonLink={practiceUrl}
      />

      <AliasStickyCta sentinelRef={heroCtaRef} practiceUrl={practiceUrl} label={stickyCtaLabel} />
    </Stack>
  );
};
